/**
 * Which way of buying this group did the restaurant actually use last?
 *
 * This replaces the "Preferred" pill the Related Products card used to show.
 * That pill read `purchase_options.is_preferred`, which is scoped to ONE
 * inventory item and carries no uniqueness constraint — so a three-member
 * EQUIVALENT group routinely showed three "Preferred" vendors, each merely
 * being the default of its own item. It answered a question nobody asked.
 *
 * "Last Used" answers a question a buyer does ask, and it is derived from
 * real purchasing evidence only:
 *
 *   1. a PURCHASE ORDER that was actually sent (purchase_orders.sent_at) and
 *      carried this option — the strongest possible evidence, because the
 *      store placed it;
 *   2. failing that, an INVOICE that was accepted for this package
 *      (purchase_options.last_operational_source_type = 'invoice_import',
 *      dated by last_operational_source_at) — the store paid for it.
 *
 * Everything else on that column is catalog housekeeping, not purchasing:
 * 'item_import', 'item_edit', 'count_item_creation', 'manual_item_creation'
 * and 'vendor_item_link' describe how a row got into the catalog, never that
 * anyone bought it. A store that has only ever imported a price list has no
 * last-used option, and the badge correctly appears nowhere.
 *
 * At most one option per group wins. Ties break toward the stronger basis,
 * then deterministically by option id, so the badge never appears twice.
 */

/** Operational source types that mean "this package was really purchased". */
export const PURCHASING_SOURCE_TYPES = Object.freeze(new Set(['invoice_import']));

const BASIS_RANK = { purchase_order: 2, invoice: 1 };

const timeOf = (value) => {
  const parsed = Date.parse(value ?? '');
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * @param {Array}  options  the pooled purchase_options of one group
 * @param {object} [ctx]    { orderUseByOptionId: { [optionId]: isoDate } } —
 *   the latest SENT purchase order per option (see
 *   services/purchaseOptionService.fetchLastOrderUseByOption). Omitted or
 *   empty simply means no order evidence is loaded.
 * @returns {{ optionId: string, at: string, basis: 'purchase_order'|'invoice' }|null}
 */
export function resolveLastUsedOption(options = [], { orderUseByOptionId = {} } = {}) {
  let best = null;
  (Array.isArray(options) ? options : []).forEach((option) => {
    const optionId = option?.id !== null && option?.id !== undefined ? String(option.id) : null;
    if (!optionId) return;

    const candidates = [];
    const orderedAt = orderUseByOptionId?.[optionId] ?? null;
    if (timeOf(orderedAt) != null) {
      candidates.push({ optionId, at: orderedAt, basis: 'purchase_order', t: timeOf(orderedAt) });
    }
    if (PURCHASING_SOURCE_TYPES.has(String(option?.last_operational_source_type ?? ''))) {
      const at = option?.last_operational_source_at ?? null;
      if (timeOf(at) != null) {
        candidates.push({ optionId, at, basis: 'invoice', t: timeOf(at) });
      }
    }

    candidates.forEach((candidate) => {
      if (best === null) { best = candidate; return; }
      if (candidate.t !== best.t) {
        if (candidate.t > best.t) best = candidate;
        return;
      }
      const rank = BASIS_RANK[candidate.basis] - BASIS_RANK[best.basis];
      if (rank > 0) { best = candidate; return; }
      if (rank === 0 && candidate.optionId < best.optionId) best = candidate;
    });
  });
  if (!best) return null;
  return { optionId: best.optionId, at: best.at, basis: best.basis };
}

/** "Last ordered 12 Aug 2026" / "Last invoiced 3 Jul 2026" — the badge's title. */
export function lastUsedTitle(lastUsed) {
  if (!lastUsed) return null;
  const when = new Date(lastUsed.at);
  const date = Number.isNaN(when.getTime())
    ? null
    : when.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const verb = lastUsed.basis === 'purchase_order' ? 'Last ordered' : 'Last invoiced';
  return date ? `${verb} ${date}` : verb;
}
